import {makeApolloClientCall} from "../../../api/makeApolloApiCall.js";
import {useCallback, useEffect, useState} from "react";
import {CATEGORIES_QUERY, DEPARTMENTS_QUERY, GEO_FAMILY_QUERY, SUBCATEGORIES_QUERY} from "./queries.js";
import axios from "axios";
import {getAccessToken} from "../../../api/getAccessToken.js";

const getValueFromEdges = (edges) => {
    return edges?.map((edge) => edge.node)
}


export const useFetchAllSubscriptions = () => {
    const [loading, setLoading] = useState(true)
    const [subscriptions, setSubscriptions] = useState([])
    const fetchAllSubscriptions = useCallback(async () => {
        return (await axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/retailers/key-accounts-approval`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        }).then((result) => {
            setSubscriptions(result.data)
        }))
    }, [])

    useEffect(() => {
        setLoading(true)
        fetchAllSubscriptions()
            .finally(() => {
                setLoading(false)
            })
    }, [fetchAllSubscriptions])

    return {subscriptions, loading, fetchAllSubscriptions}
}

export const useFetchMdmCompanies = () => {
    const [isCompaniesLoading, setIsCompaniesLoading] = useState(true);
    const [companyList, setCompanyList] = useState([])


    const fetchMdmCompanies = async () => {
        const companies = await axios.get(`${process.env.REACT_APP_RIS_API_URL}/caching/mdm/companies`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        })
        setCompanyList(companies.data);
    }

    useEffect(() => {
        fetchMdmCompanies().finally(() => setIsCompaniesLoading(false))
    }, [])


    return {isCompaniesLoading, companyList}
}

export const useFetchCplBrands = () => {
    const [brandList, setBrandList] = useState([])
    const [isBrandListLoading, setIsBrandListLoading] = useState(true);

    const fetchCplBrands = async () => {
        setIsBrandListLoading(true)
        await axios.get(`${process.env.REACT_APP_RIS_API_URL}/caching/cpl/brands`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            },
        }).then(response => {
            setBrandList(response.data.map((item) => ({
                displayName: item,
                value: item,
            })))
        })
    }
    useEffect(() => {
        fetchCplBrands().finally(() => setIsBrandListLoading(false))
    }, [])

    return {brandList, isBrandListLoading}
}

export const useFetchGeoFamilyIds = () => {
    const [geoFamilyIds, setGeoFamilyIds] = useState([])
    const [isGeoFamilyIdsLoading, setIsGeoFamilyIdsLoading] = useState(true)

    const fetchGeoFamilyIds = async () => {
        setIsGeoFamilyIdsLoading(true)
        await makeApolloClientCall(GEO_FAMILY_QUERY, false).then((response) => {
            const uniqueIds = response.geoFamilyIds.filter(function (item, pos, self) {
                return self.findIndex(i => i.geo_family_id === item.geo_family_id) === pos;
            })
            setGeoFamilyIds(uniqueIds.map((g) => {
                return {
                    value: g.geo_family_id,
                    displayName: `${g.geo_family_id} - ${g.geo_family_description}`,
                    majorChannelCode: g.major_channel_code
                }
            }))
        })
    }

    useEffect(() => {
        fetchGeoFamilyIds().finally(() => setIsGeoFamilyIdsLoading(false))
    }, []);

    return {geoFamilyIds, isGeoFamilyIdsLoading}
}

export const useFetchSubcategories = () => {
    const [subcategories, setSubcategories] = useState([])
    const [isSubcategoriesLoading, setIsSubcategoriesLoading] = useState(true);


    const fetchSubcategories = async () => {
        setIsSubcategoriesLoading(true)
        await makeApolloClientCall(SUBCATEGORIES_QUERY, false,).then((response) => {
            setSubcategories(getValueFromEdges(response.sdnDeliveryPeriodSnapshotSubcategories.edges))
        })
    }
    useEffect(() => {
        fetchSubcategories().finally(() => setIsSubcategoriesLoading(false))
    }, [])

    return {subcategories, isSubcategoriesLoading}
}
export const useFetchCategories = () => {
    const [categories, setCategories] = useState([])
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);

    const fetchCategories = async () => {
        setIsCategoriesLoading(true)
        await makeApolloClientCall(CATEGORIES_QUERY, false,).then((response) => {
            setCategories(getValueFromEdges(response.sdnDeliveryPeriodSnapshotCategories.edges).map((category) => ({
                ...category,
                subcategories: getValueFromEdges(category.subcategories.edges)
            })))
        })
    }
    useEffect(() => {
        fetchCategories().finally(() => setIsCategoriesLoading(false))
    }, [])

    return {categories, isCategoriesLoading}
}

export const useFetchDepartments = () => {
    const [departments, setDepartments] = useState([])
    const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(true);

    const fetchDepartments = async () => {
        setIsDepartmentsLoading(true)
        await makeApolloClientCall(DEPARTMENTS_QUERY, false,).then((response) => {
            setDepartments(
                getValueFromEdges(response.sdnDeliveryPeriodSnapshotDepartments.edges)
                    .map((department) => (
                        {
                            ...department,
                            categories: getValueFromEdges(department.categories.edges).map((category) => ({
                                ...category,
                                subcategories: getValueFromEdges(category.subcategories.edges)
                            }))
                        }
                    ))
            )
        })
    }
    useEffect(() => {
        fetchDepartments().finally(() => setIsDepartmentsLoading(false))
    }, [])

    return {departments, isDepartmentsLoading}
}

export const useListState = (initialState = []) => {
    const [list, setList] = useState(initialState)
    const addToList = (item) => {
        setList((prevState) => Array.isArray(item) ? [...prevState, ...item] : [...prevState, item])
    }
    const resetList = () => setList(initialState)
    const removeFromList = (...indices) => {
        setList((current) => current.filter((_, index) => !indices.includes(index)));

    }
    return [list, addToList, resetList, removeFromList]
}


export const useFetchSubscriptions = (companySfId, application = 'ignitePlus') => {
    const [activeSubscriptions, setActiveSubscriptions] = useState([])
    const [isActiveSubscriptionsLoading, setIsActiveSubscriptionsLoading] = useState(true);

    const fetchActiveSubscriptions = useCallback(async () => {
        setIsActiveSubscriptionsLoading(true)
        if (companySfId === undefined) {
            setActiveSubscriptions([])
            return
        }
        return await axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/data-subscriptions/subscriptions?companyId=${companySfId}`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            },
        })
            .then(({data}) => {
                const filteredData = data.filter(d => d.application === application)
                setActiveSubscriptions(filteredData)
            })
            .catch(() => setActiveSubscriptions([]))
            .finally(() => setIsActiveSubscriptionsLoading(false))
    }, [companySfId, application])

    useEffect(() => {
        fetchActiveSubscriptions().finally(() => setIsActiveSubscriptionsLoading(false))
    }, [fetchActiveSubscriptions])

    return {activeSubscriptions, fetchActiveSubscriptions, isActiveSubscriptionsLoading}
}


export const useFetchSubscriptionsByCompany = () => {
    const [loading, setLoading] = useState(true)
    const [subscriptions, setSubscriptions] = useState([])
    const fetchAllSubscriptions = useCallback(async () => {
        return (await axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/data-subscriptions/subscriptions-by-company`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        })).data
    }, [])

    useEffect(() => {
        fetchAllSubscriptions()
            .then((result) => {
                setSubscriptions(result)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [fetchAllSubscriptions])

    return {subscriptions, loading}
}
